import storageService from './storageService';
import {Adventure} from '../models/Adventure';
import {GameState} from '../models/GameState';

const DB_PREFIX = 'laventurier_';
const LAST_SYNC_KEY = `${DB_PREFIX}last_sync`;
const DELETED_SLOTS_KEY = `${DB_PREFIX}deleted_slots`;

class DatabaseService {
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    await storageService.init();
    this.initialized = true;
  }

  private getKey(uniqueURI: string, slot: number): string {
    return `${DB_PREFIX}${uniqueURI}_${slot}`;
  }

  private getBackupKey(uniqueURI: string, slot: number): string {
    return `${DB_PREFIX}${uniqueURI}_${slot}_backup`;
  }

  async saveGameState(gameState: GameState): Promise<void> {
    const key = this.getKey(gameState.uniqueURI, gameState.slot);
    await storageService.setItem(key, JSON.stringify(gameState));
  }

  async getGameState(uniqueURI: string, slot: number): Promise<GameState | null> {
    const key = this.getKey(uniqueURI, slot);
    const data = await storageService.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  async deleteGameState(uniqueURI: string, slot: number): Promise<void> {
    const key = this.getKey(uniqueURI, slot);
    await storageService.removeItem(key);
    // Marquer le slot comme supprimé pour la synchronisation
    await this.markSlotAsDeleted(uniqueURI, slot);
  }

  async saveBackup(gameState: GameState): Promise<void> {
    const key = this.getBackupKey(gameState.uniqueURI, gameState.slot);
    await storageService.setItem(key, JSON.stringify(gameState));
  }

  async getBackup(uniqueURI: string, slot: number): Promise<GameState | null> {
    const key = this.getBackupKey(uniqueURI, slot);
    const data = await storageService.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  async getAllAdventures(): Promise<any[]> {
    const keys = await storageService.getAllKeys();
    const adventureKeys = keys.filter(key => 
      key.startsWith(DB_PREFIX) && 
      !key.includes('_backup') &&
      key !== LAST_SYNC_KEY &&
      key !== DELETED_SLOTS_KEY
    );
    
    const items = await storageService.multiGet(adventureKeys);
    return items
      .map(([key, value]) => {
        if (!value) return null;
        try {
          return JSON.parse(value);
        } catch (error) {
          console.error(`[DatabaseService] Failed to parse JSON for key ${key}:`, error);
          console.error(`[DatabaseService] Value (first 100 chars):`, value.substring(0, 100));
          return null;
        }
      })
      .filter(Boolean);
  }

  /**
   * Récupère toutes les aventures sauvegardées localement
   */
  async getAdventures(): Promise<Adventure[]> {
    try {
      const keys = await storageService.getAllKeys();
      const adventureKeys = keys.filter((key) => key.startsWith('adventure_'));
      const adventures: Adventure[] = [];

      for (const key of adventureKeys) {
        const data = await storageService.getItem(key);
        if (data) {
          try {
            adventures.push(JSON.parse(data));
          } catch (e) {
            console.error(`[DatabaseService] Failed to parse adventure ${key}:`, e);
          }
        }
      }

      return adventures;
    } catch (error) {
      console.error('[DatabaseService] Error getting adventures:', error);
      return [];
    }
  }

  /**
   * Sauvegarde une aventure dans le localStorage
   */
  async saveAdventure(adventure: Adventure): Promise<void> {
    const key = `adventure_${adventure.uniqueURI}`;
    await storageService.setItem(key, JSON.stringify(adventure));
    console.log(`[DatabaseService] Saved adventure: ${adventure.uniqueURI}`);
  }

  /**
   * Récupère une aventure par son uniqueURI
   */
  async getAdventureByUniqueURI(uniqueURI: string): Promise<Adventure | null> {
    const key = `adventure_${uniqueURI}`;
    const data = await storageService.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Récupère tous les gameStates "dirty" (modifiés localement)
   * EXCLUT les gameStates de test (userId='test-user')
   */
  async getDirtyGameStates(): Promise<GameState[]> {
    const allStates = await this.getAllAdventures();
    // Filtrer les gameStates dirty ET exclure les gameStates de test
    return allStates.filter(
      (state: GameState) =>
        state.dirty === true &&
        state.userId !== 'test-user' &&
        !state.idApi?.startsWith('test-'),
    );
  }

  /**
   * Récupère la date de la dernière synchronisation
   */
  async getLastSync(): Promise<string | null> {
    return await storageService.getItem(LAST_SYNC_KEY);
  }

  /**
   * Met à jour la date de la dernière synchronisation
   */
  async updateLastSync(date: string): Promise<void> {
    await storageService.setItem(LAST_SYNC_KEY, date);
  }

  /**
   * Récupère les slots supprimés localement
   */
  async getDeletedSlots(): Promise<Array<{uniqueURI: string; slot: number; deletedAt: string}>> {
    const data = await storageService.getItem(DELETED_SLOTS_KEY);
    return data ? JSON.parse(data) : [];
  }

  /**
   * Ajoute un slot à la liste des slots supprimés
   */
  async markSlotAsDeleted(uniqueURI: string, slot: number): Promise<void> {
    const deletedSlots = await this.getDeletedSlots();
    const deletedAt = new Date().toISOString();
    
    // Vérifier si le slot n'est pas déjà dans la liste
    const existingIndex = deletedSlots.findIndex(
      (d) => d.uniqueURI === uniqueURI && d.slot === slot
    );
    
    if (existingIndex >= 0) {
      // Mettre à jour la date de suppression
      deletedSlots[existingIndex].deletedAt = deletedAt;
    } else {
      // Ajouter le slot
      deletedSlots.push({uniqueURI, slot, deletedAt});
    }
    
    await storageService.setItem(DELETED_SLOTS_KEY, JSON.stringify(deletedSlots));
  }

  /**
   * Efface un slot de la liste des slots supprimés
   */
  async clearDeletedSlot(uniqueURI: string, slot: number): Promise<void> {
    const deletedSlots = await this.getDeletedSlots();
    const filtered = deletedSlots.filter(
      (d) => !(d.uniqueURI === uniqueURI && d.slot === slot)
    );
    await storageService.setItem(DELETED_SLOTS_KEY, JSON.stringify(filtered));
  }

  /**
   * Efface tous les slots supprimés (après synchronisation réussie)
   */
  async clearDeletedSlots(): Promise<void> {
    await storageService.removeItem(DELETED_SLOTS_KEY);
  }

  /**
   * Récupère un gameState par sa clé (pour les backups)
   */
  async getGameStateWithKey(key: string): Promise<GameState | null> {
    const data = await storageService.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  async close(): Promise<void> {
    // Le storage service n'a pas besoin de fermeture explicite
    this.initialized = false;
  }
}

export default new DatabaseService();
